// /components/:widgetId/*: loads one widget, shows its name + tabs, and hands the
// widget to the tab pages through the outlet context.
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { NavLink, Outlet, useOutletContext, useParams } from "react-router-dom";
import { Check, CreditCard, Pencil, Save } from "lucide-react";
import { useApi, type Widget } from "../../api";
import { useBreadcrumbLabel } from "../../breadcrumbs";
import { Attention } from "../../components/Attention";
import { tabVisible, widgetTabs } from "../../sections";
import { StatusChip } from "../../components/StatusChip";
import "../../widgets.css";

/** What the header's Save / Discard buttons show for the current tab. */
interface SaveBarState {
  dirty: boolean; // unsaved changes exist
  saving: boolean;
  blocked: boolean; // changes can't be saved yet (validation problems)
}
interface SaveBarHandlers {
  onSave: () => void;
  onDiscard: () => void;
}

/** Unsaved edits per tab, kept here so they survive switching tabs. */
interface Drafts {
  values: Record<string, unknown>;
  set: (key: string, update: (prev: unknown) => unknown) => void;
}

export interface WidgetContext {
  widget: Widget;
  drafts: Drafts;
  /** Replace the loaded widget (after a save). */
  setWidget: (w: Widget) => void;
  /** Lets the active tab drive the Save / Discard buttons in the header. */
  saveBar: {
    set: (s: SaveBarState | null) => void;
    handlers: MutableRefObject<SaveBarHandlers | null>;
  };
}

/**
 * A tab's editable copy of saved data, kept by the widget layout so unsaved
 * edits survive moving between tabs. Returns [value, setValue, discard]; the
 * value is the saved data until something changes, and setting it back to the
 * saved data clears the draft (so the tab isn't marked unsaved).
 */
export function useTabDraft<T>(key: string, saved: T): [T, (next: T | ((prev: T) => T)) => void, () => void] {
  const { drafts } = useWidget();
  const savedRef = useRef(saved);
  savedRef.current = saved;
  const stored = drafts.values[key] as T | undefined;
  const value = stored === undefined ? saved : stored;
  const setValue = useCallback(
    (next: T | ((prev: T) => T)) =>
      drafts.set(key, (prev) => {
        const base = (prev === undefined ? savedRef.current : prev) as T;
        const v = typeof next === "function" ? (next as (p: T) => T)(base) : next;
        return JSON.stringify(v) === JSON.stringify(savedRef.current) ? undefined : v;
      }),
    [drafts, key],
  );
  const discard = useCallback(() => drafts.set(key, () => undefined), [drafts, key]);
  return [value, setValue, discard];
}

/** Tab pages call this to get the widget. */
export function useWidget(): WidgetContext {
  return useOutletContext<WidgetContext>();
}

/**
 * Show Save / Discard in the widget header for this tab. Handlers go in a ref
 * (they change every render); only the plain flags trigger a header re-render.
 */
export function useSaveBar({ dirty, saving, blocked, onSave, onDiscard }: SaveBarState & SaveBarHandlers) {
  const { saveBar } = useWidget();
  saveBar.handlers.current = { onSave, onDiscard };
  useEffect(() => saveBar.set({ dirty, saving, blocked }), [saveBar, dirty, saving, blocked]);
  // Hide the buttons when the tab unmounts (e.g. switching to Install)
  useEffect(() => () => saveBar.set(null), [saveBar]);
}

export function WidgetLayout() {
  const { widgetId } = useParams();
  const api = useApi();
  const [widget, setWidget] = useState<Widget | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  // Save / Discard for whichever tab is showing (set via useSaveBar)
  const [activeBar, setBar] = useState<SaveBarState | null>(null);
  const bar = activeBar ?? { dirty: false, saving: false, blocked: false };
  const handlers = useRef<SaveBarHandlers | null>(null);
  const saveBar = useMemo(() => ({ set: setBar, handlers }), []);
  const [name, setName] = useState("");
  // Unsaved edits per tab (useTabDraft); cleared when another widget opens
  const [draftValues, setDraftValues] = useState<Record<string, unknown>>({});
  useEffect(() => setDraftValues({}), [widgetId]);
  const drafts = useMemo<Drafts>(
    () => ({
      values: draftValues,
      set: (key, update) =>
        setDraftValues((d) => {
          const next = update(d[key]);
          const copy = { ...d };
          if (next === undefined) delete copy[key];
          else copy[key] = next;
          return copy;
        }),
    }),
    [draftValues],
  );

  // Breadcrumb shows the widget's name instead of its ID
  useBreadcrumbLabel(widgetId, widget?.name);

  useEffect(() => {
    if (!widgetId) return;
    setWidget(null);
    api
      .getWidget(widgetId)
      .then(setWidget)
      .catch((e: Error) => setError(e.message));
  }, [api, widgetId]);

  async function saveName() {
    if (!widget || !name.trim() || name.trim() === widget.name) return setRenaming(false);
    try {
      setWidget(await api.updateWidget(widget.widgetId, { name: name.trim() }));
      setRenaming(false);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  if (error) return <div className="flash flash-danger" role="alert">{error}</div>;
  if (!widget) return <p className="muted">Loading…</p>;

  // Payment is shown on its own (a card with a button), setup to-dos as a line
  const payTodo = widget.statusMissing.find((m) => m.key === "subscription");
  const setupTodo = widget.statusMissing.filter((m) => m.key !== "subscription");

  return (
    <div className="widget-page">
      <div className="widget-head">
        {renaming ? (
          <form
            className="rename"
            onSubmit={(e) => {
              e.preventDefault();
              void saveName();
            }}
          >
            <input className="input" value={name} maxLength={60} autoFocus onChange={(e) => setName(e.target.value)} onBlur={() => void saveName()} />
            <button type="submit" className="icon-btn" aria-label="Save name">
              <Check size={16} />
            </button>
          </form>
        ) : (
          <h1>
            {widget.name}
            <button
              type="button"
              className="icon-btn"
              aria-label="Rename component"
              title="Rename"
              onClick={() => {
                setName(widget.name);
                setRenaming(true);
              }}
            >
              <Pencil size={14} />
            </button>
          </h1>
        )}
        <StatusChip widget={widget} />

        {/* Save / Discard for the current tab, right-aligned on the title line. Always
            rendered (greyed out on tabs with nothing to save, like Install) so the
            header doesn't shift when switching tabs. */}
          <div className="widget-head-actions">
            {bar.dirty && <span className="unsaved-dot">Unsaved changes</span>}
            <button type="button" className="btn btn-secondary" onClick={() => handlers.current?.onDiscard()} disabled={!bar.dirty || bar.saving}>
              Discard
            </button>
            <span className="has-attention">
              <button type="button" className="btn btn-primary" onClick={() => handlers.current?.onSave()} disabled={!bar.dirty || bar.saving || bar.blocked}>
                <Save size={16} aria-hidden="true" /> {bar.saving ? "Saving…" : bar.dirty ? "Save" : "Saved"}
              </button>
              {/* Unsaved changes on this tab: point at Save */}
              {bar.dirty && !bar.saving && <Attention corner="tr" label="Unsaved changes: save them" />}
            </span>
          </div>
      </div>

      {/* Draft: setup still to do (not payment), each linking to its tab */}
      {setupTodo.length > 0 && (
        <p className="setup-todo">
          <strong>Not running yet.</strong> To do:{" "}
          {setupTodo.map((m, i) => (
            <span key={m.key}>
              {i > 0 && " · "}
              <NavLink to={m.tab}>{m.text}</NavLink>
            </span>
          ))}
        </p>
      )}

      {/* Not paid for: it can't go live on a website or hosted page until it's on the plan.
          Once everything else is set up, the pulsing star points at Billing. */}
      {payTodo && (
        <div className="golive-card">
          <div>
            <strong>{setupTodo.length ? "Then, to go live:" : "Ready to go live"}</strong>
            <span>
              {payTodo.text}. Until then it only works on the test page, not on your {widget.branding?.layout.style === "hosted" ? "hosted page" : "website"}.
            </span>
          </div>
          <span className="has-attention">
            <NavLink to="/billing" className="btn btn-primary">
              <CreditCard size={16} aria-hidden="true" /> Go to Billing
            </NavLink>
            {!setupTodo.length && <Attention corner="tr" label="Pay to go live" />}
          </span>
        </div>
      )}

      <nav className="tabs" aria-label="Component settings">
        {/* The Hosted page tab only exists for hosted widgets */}
        {widgetTabs
          // Hosted page, contact form, estimate-only and heat pump / solar-only tabs
          // show for those components only (sections.ts)
          .filter((t) => tabVisible(t, widget.branding?.layout.style ?? (widget.product === "solar" ? "full" : "small"), widget.product ?? "ashp"))
          .map((t) => (
          <NavLink key={t.path} to={t.path} className="tab">
            <t.icon size={15} aria-hidden="true" />
            {t.label}
            {/* This tab has edits that aren't saved yet */}
            {t.path in draftValues && <span className="tab-unsaved" title="Unsaved changes" aria-label="Unsaved changes" />}
          </NavLink>
        ))}
      </nav>

      <Outlet context={{ widget, setWidget, saveBar, drafts } satisfies WidgetContext} />
    </div>
  );
}
